<template>
  <div class="mission-asset-detail" ref="container">
    <v-overlay absolute :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

      <v-list v-if="missionAsset" dense :style="`max-height: ${scrollableHeight}px`" class="overflow-y-auto mb-2">
        <v-subheader>Asset</v-subheader>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>Asset ID</v-list-item-title>
          </v-list-item-content>
          <v-list-item-content>
            <v-list-item-subtitle>
              <span v-if="missionAsset.thirdPartyCode">{{ missionAsset.thirdPartyCode }}</span>
                <v-tooltip v-else top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ missionAsset.id }}
                      <v-icon color="orange">mdi-alert-circle</v-icon>
                    </span>
                  </template>
                  External asset data unavailable
                </v-tooltip>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>Asset Type</v-list-item-title>
          </v-list-item-content>
          <v-list-item-content>
            <v-list-item-subtitle>
              {{
                missionAsset.asset && missionAsset.asset.code
                  ? missionAsset.asset.code.name
                  : ""
              }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-expansion-panels v-model="mission_panel_open" accordion focusable>
        <template v-for="(ma, i) in missionAssetsWithSameAssetSorted">
          <v-expansion-panel v-if="show_missions_out_of_range || missionIdsInsideRange.indexOf(ma.mission.id) >= 0" :key="`expanel_${ma.id}`">
            <v-expansion-panel-header>
              <div class="d-flex justify-start align-center">
                <span>Mission {{ ma.mission.id }}</span>
                <v-chip label small color="grey darken-3" class="ml-2">
                {{ ma.mission.createdAt | dateLocal("DD-MMM-YYYY") | toUpperCase }}
                <v-icon v-if="missionIdsInsideRange.indexOf(ma.mission.id) < 0" small right>
                  mdi-information
                </v-icon>
                </v-chip>
                <v-chip v-if="ma.passFail == 'pass'" label small color="green" class="ml-2">{{ getTerminology("pass") || "PASS" }}</v-chip>
                <v-chip v-if="ma.passFail == 'fail'" label small color="red" class="ml-2">{{ getTerminology("fail") || "FAIL" }}</v-chip>
                <v-tooltip v-if="ma.isFault" top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-chip v-if="ma.isFault" label small color="red" class="ml-2">FAULT</v-chip>
                      <!--<v-chip v-else label small color="green" class="ml-2">NO FAULT</v-chip>-->
                    </span>
                  </template>
                  <span v-if="ma.faults.length > 0">{{ getMissionAssetFaultsString(ma) }}</span>
                  <span v-else>Fault data unavailable</span>
                </v-tooltip>
                <span class="ml-2">
                  <mission-asset-faults-resolved-toggle :mission-asset="ma" @mission-asset-updated="onMissionAssetUpdated"></mission-asset-faults-resolved-toggle>
                </span>
                <!-- The new asset chip only goes on the earliest mission since that's when the asset was new -->
                <v-chip v-if="i === 0 && ma.newAsset" label small light color="yellow" class="ml-2">NEW</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Mission</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ ma.mission.id }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Date</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      {{ ma.mission.createdAt | dateLocal("DD-MMM-YYYY") | toUpperCase }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </template>
      </v-expansion-panels>

      <p v-if="missionIdsOutsideRange.length > 0" class="my-4">
        There are missions ({{ missionIdsOutsideRange.length }}) that fall outside the requested date range.
        <v-btn small light @click="show_missions_out_of_range = !show_missions_out_of_range">
          {{ show_missions_out_of_range ? 'HIDE' : 'SHOW' }}
        </v-btn>
      </p>

  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { denormalize } from "@/helpers/JsonApiDenormalizer";
import { MissionAssetService } from "@/services/missionAsset.service";
import { AssetService } from "@/services/asset.service";
import { UserService } from "@/services/user.service";
import MissionAssetFaultsResolvedToggle from "@/components/MissionAsset/MissionAssetFaultsResolvedToggle";

export default {
  components: {
    MissionAssetFaultsResolvedToggle
  },
  props: {
    missionAssetId: {
      required: true,
    },
  },
  data: () => ({
    missionAssetsWithSameAsset: [],
    missionAsset: null,
    mission_panel_open: undefined, // zero-based index of which panel is open
    show_missions_out_of_range: false,
    scrollableHeight: 0,
    loading: false,
  }),
  computed: {
    ...mapGetters("config", ["getTerminology"]),
    missionAssetsWithSameAssetSorted() {
      // We'll sort these by most recent parent mission first
      let missionAssets = [];
      this.missionAssetsWithSameAsset.forEach(ma => {
        missionAssets.push(Object.assign({}, ma));
      });
      missionAssets.sort((ma1, ma2) => {
        let moment1 = moment(ma1.mission.createdAt);
        let moment2 = moment(ma2.mission.createdAt);
        let unix1 = moment1.format('X');
        let unix2 = moment2.format('X');
        if (unix1 === unix2) return 0;
        if (unix1 > unix2) return -1;
        if (unix1 < unix2) return 1;
      });
      return missionAssets;
    },
    missionIdsInsideRange() {
      let ids = [];
      let filterDateStart = this.$store.state.filters.missions.dateStart;
      let filterDateEnd = this.$store.state.filters.missions.dateEnd;
      filterDateStart = filterDateStart
        ? moment(filterDateStart)
        : null;
      filterDateEnd = filterDateEnd
        ? moment(filterDateEnd)
        : null;
      if (filterDateStart && ! filterDateStart.isValid()) filterDateStart = null;
      if (filterDateEnd && ! filterDateEnd.isValid()) filterDateEnd = null;
      if (filterDateStart || filterDateEnd) {
        this.missionAssetsWithSameAsset.forEach(ma => {
          const missionDateStart = moment(ma.mission.createdAt);
          if (filterDateStart && ! filterDateEnd) {
            if (filterDateStart.isSameOrBefore(missionDateStart, 'minute')) {
              ids.push(ma.mission.id);
            }
          }
          else if (filterDateEnd && ! filterDateStart) {
            if (filterDateEnd.isSameOrAfter(missionDateStart, 'minute')) {
              ids.push(ma.mission.id);
            }
          }
          else if (filterDateStart && filterDateEnd) {
            // https://momentjs.com/docs/#/query/is-between/
            if (missionDateStart.isBetween(filterDateStart, filterDateEnd, 'minute', '[]')) {
              ids.push(ma.mission.id);
            }
          }
        });
      }
      else {
        // No date range filters applied so all considered to be within range
        this.missionAssetsWithSameAsset.forEach(ma => {
          ids.push(ma.mission.id);
        });
      }
      return ids;
    },
    missionIdsOutsideRange() {
      let ids = [];
      this.missionAssetsWithSameAsset.forEach(ma => {
        if (this.missionIdsInsideRange.indexOf(ma.mission.id) < 0) {
          ids.push(ma.mission.id);
        }
      });
      return ids;
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.setScrollableHeight();
  },
  watch: {
    missionAssetId() {
      this.fetchData();
    },
    missionAssetsWithSameAsset(nextItems) {
      if (nextItems.length > 0) {
        this.$nextTick(() => {
          this.mission_panel_open = 0;
        });
      }
    }
  },
  methods: {
    setScrollableHeight() {
      setTimeout(() => {
        const containerHeight = this.$refs.container.clientHeight;
        this.scrollableHeight = containerHeight;
      }, 500);
    },
    async fetchData() {
      try {
        this.loading = true;
        const missionAsset = await MissionAssetService.find(
          this.missionAssetId, { include: 'mission' }
        )
        .then(response => denormalize(response.data));
        const missionAssetsWithSameAsset = await(MissionAssetService.all({
          core_id: missionAsset.coreId,
          include: 'mission,newAssetLog',
        })).then(response => denormalize(response.data));
        // Fetch stormport asset data
        if (missionAsset.coreId) {
          try {
            missionAsset.asset = await AssetService.find(
              missionAsset.coreId
            ).then((response) => denormalize(response.data));
          } catch (err) {}
        }
        this.missionAsset = missionAsset
        this.missionAssetsWithSameAsset = missionAssetsWithSameAsset
      }
      catch(err) {
        window.console && console.log(err);
      }
      finally {
        this.loading = false;
      };
    },
    getMissionAssetFaultsString(missionAsset) {
      let fault_names = missionAsset.faults.map(fault => {
        return fault.name;
      });
      return fault_names.join(', ') || 'None';
    },
    teleportToMission(mission) {
      this.$store.commit("missions/SET_SELECTED", mission);
      this.$router.push({ name: "missions" });
    },
    onMissionAssetUpdated(ma) {
      if (this.missionAsset && this.missionAsset.id) {
        let missionAssetUpdated = Object.assign({}, this.missionAsset);
        Object.keys(ma).forEach(key => {
          missionAssetUpdated[key] = ma[key];
        });
        this.missionAsset = missionAssetUpdated;
      }
    },
  },
};
</script>

<style lang="scss">
.mission-asset-detail {
  height: calc(100% - 250px); // less map height
  // height: 500px;
  position: relative;
  -webkit-overflow-scrolling: touch;
}
</style>
